import { formatCheckedOn } from "../../components/home/home-content"
import type { Guide } from "../guide-types"

// Facts re-read on the ATO pages below on 2026-10-29 (tax invoices page "Last updated 18 September
// 2026"; registering for GST page "Last updated 14 September 2026"). Re-open both and bump CHECKED
// when you edit. Nothing here comes from outside those two pages.
const CHECKED = "2026-10-29"
const GST = "https://www.ato.gov.au/businesses-and-organisations/gst-excise-and-indirect-taxes/gst"
const INVOICES_URL = `${GST}/tax-invoices`
const REGISTER_URL = `${GST}/registering-for-gst`

const link =
  "focus-visible:ring-ring rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"

const H2 = ({ id, children }: { id: string; children: string }) => (
  <h2 id={id} className="text-2xl font-semibold">
    {children}
  </h2>
)

export const taxInvoiceRequirementsGuide: Guide = {
  slug: "tax-invoice-requirements",
  title: "ATO tax invoice requirements for sole traders",
  description:
    "What a tax invoice must show under ATO rules: the 7 details, when the buyer's identity is needed, when to issue one, and when to register for GST.",
  crumb: "Tax invoice requirements",
  h1: "ATO tax invoice requirements, in plain English",
  published: CHECKED,
  reviewed: CHECKED,
  financialYear: "2026–27",
  checked: formatCheckedOn(CHECKED),
  sources: [
    { label: "ATO, tax invoices", href: INVOICES_URL },
    { label: "ATO, registering for GST", href: REGISTER_URL },
  ],
  answer: (
    <>
      If a customer asks for a tax invoice you must provide one within 28 days, unless the sale is
      A$82.50 (including GST) or less. For a taxable sale under A$1,000 the invoice must show seven
      details: that it is a tax invoice, your identity and ABN, the date, what was sold with the
      quantity and price, the GST amount, and how much of each sale is taxable. From A$1,000 it must
      also show the buyer&apos;s identity or ABN. You must register for GST once your GST turnover
      reaches A$75,000.
    </>
  ),
  related: [
    { label: "Invoicing for sole traders", href: "/invoicing" },
    { label: "Free invoice generator", href: "/invoice-generator" },
    { label: "Rideshare and delivery drivers", href: "/rideshare-drivers" },
    {
      label: "ATO logbook requirements for car expenses",
      href: "/guides/ato-logbook-requirements",
    },
    { label: "ATO cents per km rate history", href: "/guides/ato-cents-per-km-rate-history" },
  ],
  toc: [
    { id: "when", label: "When you must issue one" },
    { id: "under-1000", label: "Sales under A$1,000" },
    { id: "over-1000", label: "Sales of A$1,000 or more" },
    { id: "mixed", label: "Taxable and non-taxable items" },
    { id: "digital", label: "Digital invoices" },
    { id: "rounding", label: "Rounding GST" },
    { id: "register", label: "When to register for GST" },
  ],
  body: (
    <>
      <section aria-labelledby="when" className="grid gap-3">
        <H2 id="when">When you must issue one</H2>
        <p>
          A tax invoice is the document a GST-registered seller gives a customer for a taxable sale.
          The ATO says that if a customer asks for one, you must provide it within 28 days, unless
          the sale is for A$82.50 (including GST) or less. What it has to include depends on the
          sale amount, the sale type (for example, a sale with both taxable and non-taxable items)
          and who issues it.
        </p>
        <p>
          To see how this looks in an invoicing app, read{" "}
          <a href="/invoicing" className={link}>
            Invoicing for sole traders
          </a>
          .
        </p>
      </section>

      <section aria-labelledby="under-1000" className="grid gap-3">
        <H2 id="under-1000">Sales under A$1,000</H2>
        <p>
          A tax invoice for a taxable sale of less than A$1,000 must include enough information to
          clearly determine these seven details:
        </p>
        <ol className="list-decimal space-y-1 pl-6">
          <li>the document is intended to be a tax invoice</li>
          <li>the seller&apos;s identity</li>
          <li>the seller&apos;s Australian business number (ABN)</li>
          <li>the date the invoice was issued</li>
          <li>
            a brief description of the items sold, including the quantity (if applicable) and the
            price
          </li>
          <li>
            the GST amount (if any) payable. It can be shown separately or, if the GST is exactly
            one-eleventh of the total price, as a statement that says &ldquo;Total price includes
            GST&rdquo;
          </li>
          <li>the extent to which each sale on the invoice is a taxable sale</li>
        </ol>
      </section>

      <section aria-labelledby="over-1000" className="grid gap-3">
        <H2 id="over-1000">Sales of A$1,000 or more</H2>
        <p>
          Tax invoices for sales of A$1,000 or more must also show the buyer&apos;s identity or ABN.
          The ATO notes that if your invoices meet the requirements for sales of A$1,000 or more,
          you can use them for smaller sales too. One template that meets the larger-sale rule
          therefore works for every sale.
        </p>
      </section>

      <section aria-labelledby="mixed" className="grid gap-3">
        <H2 id="mixed">Taxable and non-taxable items</H2>
        <p>
          If one invoice has taxable and non-taxable items, it must clearly show which items are
          taxable. Items are non-taxable if they are GST-free or input-taxed. The invoice must also
          show each taxable sale, the amount of GST to be paid and the total amount to be paid.
        </p>
      </section>

      <section aria-labelledby="digital" className="grid gap-3">
        <H2 id="digital">Digital invoices</H2>
        <p>
          A tax invoice doesn&apos;t need to be on paper. The ATO gives emailing an invoice as a PDF
          as an example. Whatever you send must contain all the required information to be a valid
          tax invoice.
        </p>
      </section>

      <section aria-labelledby="rounding" className="grid gap-3">
        <H2 id="rounding">Rounding GST</H2>
        <p>
          When GST includes a fraction of a cent, special rounding rules apply. With one taxable
          sale on the invoice, round the GST to the nearest cent, rounding 0.5 cents upwards. With
          more than one taxable sale there are two methods, the total invoice rule and the taxable
          supply rule, and the ATO page sets out both. You and your customers don&apos;t need to use
          the same one.
        </p>
      </section>

      <section aria-labelledby="register" className="grid gap-3">
        <H2 id="register">When to register for GST</H2>
        <p>You must register for GST:</p>
        <ul className="list-disc space-y-1 pl-6">
          <li>
            when your GST turnover (gross business income minus GST) is A$75,000 or more, which the
            ATO calls the GST threshold
          </li>
          <li>
            when you start a business and expect turnover to reach the threshold in the first year
          </li>
          <li>
            when you provide taxi or limousine travel for passengers, including ride-sourcing,
            whatever your turnover
          </li>
          <li>if you want to claim fuel tax credits</li>
        </ul>
        <p>
          Once you are required to register you have 21 days to do it. Otherwise registering is
          optional, and if you choose to, you generally must stay registered for at least 12 months.
          You need an ABN first. Your GST turnover is your total business income, not your profit,
          minus GST and a few exclusions the ATO lists, such as input-taxed sales and sales not
          connected with Australia. Check it each month against the current month plus the previous
          11, and your projected turnover for the current month plus the next 11.
        </p>
        <p>
          This guide is general information from the ATO pages above, not tax advice. For your own
          situation, check the ATO pages or ask a registered tax or BAS agent.
        </p>
      </section>
    </>
  ),
}
